import { useMemo } from 'react'
import { isAfter, isEqual, startOfDay } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { slugs } from '@/constants/slugs'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import { GiveawayFaq, useGiveawayFaqs } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayFaqs'

export enum GiveawayTabEnum {
  Details = 'Details',
  Updates = 'Updates',
  Faq = 'Faq',
  Prizes = 'Prizes',
  VacationPackages = 'VacationPackages',
}

export type GiveawayHowToStep = { title: string; description: string }
export type PartnerLogo = { src: string; alt: string }
export type GiveawayPrizeItem = { image: string; alt: string; title: string; value: string }
export type GalleryImage = { src: string; alt: string }
export type GiveawayTab = { title: string; type: GiveawayTabEnum }
export type GiveawayShare = { shareText: string; hashtag: string; shareImage: string }
export type CongratsModal = { text: string; image: string; alt: string }
export type SummaryPage = { image: string; alt: string }
export type ReservationCompletePage = { desktopImage: string; mobileImage: string; alt: string; text: string }

export type SuperFanBenefit = {
  badgeText?: string
  badgeClassName?: string
  imageUrl: string
  title: string
  description: string
  disclaimer?: string
}

export type GiveawayType = {
  tabs: GiveawayTab[]
  ticketFlow?: {
    congratsModal: CongratsModal
    summaryPage: SummaryPage
    reservationCompletePage: ReservationCompletePage
  }
  seo: {
    cloudinaryImagePath: string
    cloudinaryTransformation: string
    title: string
    description: string
    locale: string
    path: string
  }
  share: GiveawayShare
  endDate: Date
  winnerAnnouncedDate: Date
  giveawayValue?: string
  isActive: boolean
  trailerUrl: string
  giveawayTrailerUrl: string
  logoUrl: string
  inTheatersText: string
  videoButtonText: string
  backgroundVideoSrc?: string
  rulesLink: string
  heroTitle: string
  projectName: string
  backgroundHeroImage: {
    mobile: string
    desktop: string
  }
  steps: GiveawayHowToStep[]
  partnerLogos?: PartnerLogo[]
  faqs?: GiveawayFaq[]
  prizes?: GiveawayPrizeItem[]
  superFanBenefits?: SuperFanBenefit[]
  galleryImages: GalleryImage[]
  productTitleToFilter: string
  defaultTicketPackId: string
  entriesProductId: string
  videoImage?: string
}

interface Props {
  theatricalSlug: string
  isGiveawayRoute?: boolean
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useGiveawayData = ({ theatricalSlug, isGiveawayRoute }: Props): GiveawayType | null => {
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()

  const faqs = useGiveawayFaqs(theatricalSlug)

  const data = useMemo((): Omit<GiveawayType, 'isActive'> | null => {
    if (theatricalSlug === slugs.homestead) {
      return {
        tabs: [
          {
            title: t('prizeItems', 'Prize Items'),
            type: GiveawayTabEnum.Prizes,
          },
          {
            title: t('details', 'Details'),
            type: GiveawayTabEnum.Details,
          },
          {
            title: t('updates', 'Updates'),
            type: GiveawayTabEnum.Updates,
          },
          {
            title: t('faq', 'FAQ'),
            type: GiveawayTabEnum.Faq,
          },
        ],
        ticketFlow: {
          congratsModal: {
            text: t(
              'btdCongratsModalText',
              "You've earned entries for your chance to win a $100k Dream Vacations for You and Your Favorite Teacher! Complete your checkout to secure your entries.",
            ),
            image: '/v1733333895/100k-vacation-image-02.webp',
            alt: '',
          },
          summaryPage: {
            image: '/v1733333895/100k-vacation-image-02.webp',
            alt: '',
          },
          reservationCompletePage: {
            desktopImage: '/v1733333895/100k-vacation-image-02.webp',
            mobileImage: '/v1733333895/100k-vacation-image-02.webp',
            alt: '',
            text: t(
              'btdReservationCompleteText',
              'You’ve earned entries into the Brave the Dark Giveaway for a chance to win the $100k Dream Vacations for You and Your Favorite Teacher!',
            ),
          },
        },
        share: {
          shareText:
            'The upcoming @AngelStudios movie Homestead in theaters on Dec. 20th. Pre-order tickets for a chance to win $300,000 in the #HomesteadSweepstakes.',
          hashtag: '#HomesteadSweepstakes',
          shareImage: 'v1730306592/homestead-key-art-main-wide-02.webp',
        },
        seo: {
          cloudinaryImagePath: 'v1730485364/Giveaway_Banner_Edit.webp',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('ticketsSEODescriptionPreSalesV2', 'PRE-ORDER {{projectName}} Tickets | Angel Studios', {
            projectName: 'Homestead',
          }),
          description: t(
            'homesteadTicketsSeoTitle',
            'Buy {{projectName}} tickets for a Chance to Win a $300K Giveaway!',
            {
              projectName: 'Homestead',
            },
          ),
          locale,
          path: isGiveawayRoute ? `/giveaway/${theatricalSlug}` : `/pre-sales/${theatricalSlug}/giveaway`,
        },
        endDate: new Date(2024, 11, 22),
        winnerAnnouncedDate: new Date(2025, 0, 30),
        giveawayTrailerUrl: 'https://www.youtube-nocookie.com/embed/V5zQ6XyHhrs',
        trailerUrl: 'https://www.youtube-nocookie.com/embed/XURLw2XXHWA',
        logoUrl:
          'https://images.angelstudios.com/image/upload/v1703029504/studio-app/catalog/df2af424-2494-49f8-af2a-2299baa0b0cf.png',
        productTitleToFilter: 'Homestead Ticket',
        defaultTicketPackId: 'gid://shopify/Product/9042410799335',
        entriesProductId: 'gid://shopify/ProductVariant/50567413891303',
        backgroundVideoSrc: 'https://content.angelstudios.com/homestead/btd-video-loop-02.mp4',
        backgroundHeroImage: {
          mobile: 'https://images.angelstudios.com/image/upload/v1730306319/homestead-key-art-main-tall-02.webp',
          desktop: 'https://images.angelstudios.com/image/upload/v1730306263/homestead-key-art-main-wide-01.webp',
        },
        rulesLink: 'https://bit.ly/HomesteadSweeps',
        heroTitle: t('enterHomesteadGiveawayV3', 'Watch Homestead in Theaters and You could Win a $300k Homestead!'),
        inTheatersText: t('inTheatersDec20', 'In theaters dec. 20th'),
        videoButtonText: t('watchGiveawayVideo', 'Watch Giveaway Video'),
        projectName: 'Homestead',
        steps: [
          {
            title: t('preOrderTicketsStep1Title', 'Pre-Order Tickets'),
            description: t(
              'preOrderTicketsStep1Description',
              'Choose a ticket pack from our exclusive pre-order options.',
            ),
          },
          {
            title: t('preOrderTicketsStep2Title', 'Get Your Promo Codes'),
            description: t(
              'preOrderTicketsStep2Description',
              'Receive promo codes equal to your ticket quantity, redeemable when showtimes are available.',
            ),
          },
          {
            title: t('preOrderTicketsStep3Title', 'Multiply Your Chances'),
            description: t(
              'preOrderTicketsStep3Description',
              'The earlier you pre-order, the higher your multiplier for the $300k Homestead Giveaway.',
            ),
          },
        ],
        partnerLogos: [
          {
            src: '/v1727730787/Homestead/partners/boxhouse-logo-gray-2x.png',
            alt: 'boxhouse-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/black-beard-logo-gray-2x.png',
            alt: 'black-beard-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/bakcou-logo-gray-2x.png',
            alt: 'bakcou-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/black-rifle-coffee-company-logo-gray-2x.png',
            alt: 'black-rifle-coffee-company-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/gatorz-logo-gray-2x.png',
            alt: 'gatorz-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/goldbacks-logo-gray-2x.png',
            alt: 'goldbacks-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/fix-it-sticks-logo-gray-2x.png',
            alt: 'fix-it-sticks-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/grip6-logo-gray-2x.png',
            alt: 'grip6-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/knafs-logo-gray-2x.png',
            alt: 'knafs-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/lion-energy-logo-gray2.png',
            alt: 'lion-energy-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/kaito-logo-gray-2x.png',
            alt: 'kaito-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/mira-safety-logo-gray-2x.png',
            alt: 'mira-safety-logo',
          },
          {
            src: '/v1727730788/Homestead/partners/my-medic-logo-gray-2x.png',
            alt: 'my-medic-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/nutrimill-logo-gray-2x.png',
            alt: 'nutrimill-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/oberwerk-logo-gray-2x.png',
            alt: 'oberwerk-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/nutristore-logo-gray-2x.png',
            alt: 'nutristore-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/olli-logo-gray-2x.png',
            alt: 'olli-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/planta-logo-gray-2x.png',
            alt: 'planta-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/protek-logo-gray-2x.png',
            alt: 'protek-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/rockwell-watch-logo-gray-2x.png',
            alt: 'rockwell-watch-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/rockwell-water-logos-gray-2x.png',
            alt: 'rockwell-water-logo',
          },
          {
            src: '/v1727730791/Homestead/partners/sparks-motors-logo-gray-2x.png',
            alt: 'sparks-motors-logo',
          },
          {
            src: '/v1727730793/Homestead/partners/thirsty-earth-logo-gray-2x.png',
            alt: 'thirsty-earth-logo',
          },
          {
            src: '/v1727730793/Homestead/partners/uncharted-supply-logo-gray2.png',
            alt: 'uncharted-supply-logo',
          },
          {
            src: '/v1727730793/Homestead/partners/x-tac-logo-gray-2x.png',
            alt: 'x-tac-logo',
          },
        ],
        faqs,
        prizes: [
          {
            image: '/v1727735733/Homestead/prizes/homestead-boxhouse-01.webp',
            alt: 'Box House',
            title: 'Box House 500 Sq. Ft. Tiny House',
            value: '$139,000',
          },
          {
            image: '/v1727735730/Homestead/prizes/DSC06697.webp',
            alt: '',
            title: 'Polaris Xpedition OHV Customized by the Diesel Brothers',
            value: '$75,000',
          },
          {
            image: '/v1727735729/Homestead/prizes/bitcoin-01.webp',
            alt: '',
            title: 'One Bitcoin (BTC)',
            value: '> $90,000',
          },
          {
            image: '/v1727735723/Homestead/prizes/AngelStudio_09172413178.webp',
            alt: '',
            title: 'Bakcou Full Suspension eBike',
            value: '$5,200',
          },
          {
            image: '/v1727735721/Homestead/prizes/AngelStudio_09172413077.webp',
            alt: '',
            title: 'NutriStore Freeze Dried 6-Month Food Supply',
            value: '$4,438',
          },
          {
            image: '/v1727735740/Homestead/prizes/sungrow-greenhouse-sungrow-20.webp',
            alt: '',
            title: 'Planta Greenhouses - Sungrow 20',
            value: '$3,800',
          },
          {
            image: '/v1727735737/Homestead/prizes/IMG_8556.webp',
            alt: '',
            title: 'PlayXTAC Four Person Tactical Laser Airsoft Kit',
            value: '$2,304',
          },
          {
            image: '/v1727735735/Homestead/prizes/homestead-rockwell-water-01.webp',
            alt: '',
            title: 'Two 250-Gallon QuakeTank Water Storage Tanks',
            value: '$1,800',
          },
          {
            image: '/v1727735725/Homestead/prizes/AngelStudio_09172413239.webp',
            alt: '',
            title: 'Lion Energy Portable Solar Power Unit (Lion Safari)',
            value: '$1,500',
          },
          {
            image: '/v1727735725/Homestead/prizes/AngelStudio_09172413261.webp',
            alt: '',
            title: 'Knafs Lulu Knife + Gear Bundle',
            value: '$750',
          },
          {
            image: '/v1727735722/Homestead/prizes/AngelStudio_09172413112.webp',
            alt: '',
            title: 'NutriMill - Mill & Mixer',
            value: '$749',
          },
          {
            image: '/v1727735726/Homestead/prizes/AngelStudio_09172413276.webp',
            alt: '',
            title: 'Thirsty Earth: CottaCup Automatic Olla Watering System & Three Planter Boxes',
            value: '$700',
          },
          {
            image: '/v1727735728/Homestead/prizes/AngelStudio_09172413351.webp',
            alt: '',
            title: 'Three Pair of GATORZ Eyewear',
            value: '$650',
          },
          {
            image: '/v1727735732/Homestead/prizes/DSC07534.webp',
            alt: '',
            title: 'Oberwerk 20x65 ED Binocular on Oberwerk 4000 Tripod',
            value: '$600',
          },
          {
            image: '/v1727735723/Homestead/prizes/AngelStudio_09172413192.webp',
            alt: '',
            title: 'Uncharted Supply Co - SEVENTY2 Pro Survival System',
            value: '$589',
          },
          {
            image: '/v1727735722/Homestead/prizes/AngelStudio_09172413091.webp',
            alt: '',
            title: '100 Utah GoldBacks - Pure Gold',
            value: '$542',
          },
          {
            image: '/v1729197021/IMG_8555.webp',
            alt: '',
            title: 'Rockwell Time - Ruck Backpack, Sunglasses, Apparel',
            value: '$500',
          },
          {
            image: '/v1729193204/plusefire-flamethrower-01.webp',
            alt: '',
            title: 'Exothermic Technologies PulseFire Long Range Torch',
            value: '$499',
          },
          {
            image: '/v1727735731/Homestead/prizes/DSC07533.webp',
            alt: '',
            title: 'ISOtunes Hearing Protection - Four Advance BT Earbuds + Two Defy Slim Basic Earmuffs',
            value: '$460',
          },
          {
            image: '/v1727735729/Homestead/prizes/AngelStudio_09172413393.webp',
            alt: '',
            title: 'PROTEK Watches USMC Dive Watch',
            value: '$450',
          },
          {
            image: '/v1727735727/Homestead/prizes/AngelStudio_09172413308.webp',
            alt: '',
            title: 'MIRA Safety EvakPak',
            value: '$370',
          },
          {
            image: '/v1727735733/Homestead/prizes/homestead-grip6-gear-01.webp',
            alt: '',
            title: 'GRIP6 - Belts, Wallets, and Socks',
            value: '$350',
          },
          {
            image: '/v1727735727/Homestead/prizes/AngelStudio_09172413344.webp',
            alt: '',
            title: 'Fix It Sticks - All-in-One Toolkit',
            value: '$292',
          },
          {
            image: '/v1727735735/Homestead/prizes/IMG_8483.webp',
            alt: '',
            title: 'MyMedic MyFAK Pro Full Medical Kit',
            value: '$227',
          },
          {
            image: '/v1727735724/Homestead/prizes/AngelStudio_09172413223.webp',
            alt: '',
            title: 'Black Beard Fire - Ultimate Fire Kit',
            value: '$225',
          },
          {
            image: '/v1727735738/Homestead/prizes/olli-apparel-img-01.webp',
            alt: '',
            title: 'Olli Bottoms of Your Choice, Olli Top of Your Choice, and Olli Headband',
            value: '$137',
          },
          {
            image: '/v1727735738/Homestead/prizes/katio-radio-02.webp',
            alt: '',
            title: 'Kaito Voyager Pro KA600',
            value: '$90',
          },
          {
            image: '/v1727735722/Homestead/prizes/AngelStudio_09172413103.webp',
            alt: '',
            title: 'Black Rifle Coffee Gift Pack',
            value: '$70',
          },
        ],
        superFanBenefits: [
          {
            badgeText: t('digitalBookBenefit', 'Digital Book'),
            imageUrl: 'https://images.angelstudios.com/image/upload/v1730479729/book-cover-perspective-view-03.jpg',
            title: t('digitalBookTitle', 'Digital Book - Homestead: Black Autumn. Faith and Family Edition'),
            description: t(
              'digitalBookDescription',
              'Prepare for the film event in the best way you can, with the bestselling novel that started it all.',
            ),
          },
          {
            badgeText: t('audioBookBenefit', 'Audio Book'),
            badgeClassName: '!bg-[#F0EEE7]',
            imageUrl: 'https://images.angelstudios.com/image/upload/v1730479680/homestead-audio-book-cover-01.png',
            title: t('audioBookTitle', 'Audio Book - Homestead: Black Autumn. Faith and Family Edition'),
            description: t(
              'audioBookDescription',
              'Listen anywhere to the bestselling novel behind Homestead, and dive into the film’s world before you even pick up your popcorn.',
            ),
          },
          {
            imageUrl: 'https://images.angelstudios.com/image/upload/v1725484357/name-in-credits-img-02.webp',
            title: t('nameInCreditsTitleV2', 'Your Name in the Credits for Streaming'),
            description: t(
              'nameInCreditsDescriptionV2',
              'Make your mark on Homestead’s release. Have your name featured in the credits—on our streaming platforms, DVD/Blu-ray, and alongside the community who brought this film to life.',
            ),
          },
          {
            badgeText: t('eBookBenefit', 'e-Book'),
            badgeClassName: '!bg-[#F0EEE7]',
            imageUrl:
              'https://images.angelstudios.com/image/upload/v1726153403/angel-studios/guild/homestead/homestead-prep-bundle-mock-01.webp',
            title: t('eBookBundleTitle', 'eBook Bundle - Homesteading & Preparedness Resource Library'),
            description: t(
              'eBookBundleDescription',
              'Enhance your library with the ultimate DIY homesteading and preparedness collection. This comprehensive eBook bundle equips you with essential skills for any just-in-case scenario.',
            ),
          },
          {
            imageUrl: 'https://images.angelstudios.com/image/upload/v1725484408/nmcd-red-carpet-image-01.webp',
            title: t('redCarpetPremiereTitle', 'Two Red Carpet Tickets to the Homestead Movie Premiere in Utah'),
            description: t(
              'redCarpetPremiereDescription',
              'Strut the red carpet like the film insider you are, and enjoy a once-in-a-lifetime experience when you attend the Utah premiere of Homestead!',
            ),
            disclaimer: t('redCarpetPremiereDisclaimer', 'Does not include travel, lodging, and food expenses.'),
          },
          {
            imageUrl:
              'https://images.angelstudios.com/image/upload/v1726078846/angel-studios/guild/homestead/homestead-site-01.webp',
            title: t('setVisitTitle', 'Invitation to Visit the Homestead Property During Filming'),
            description: t(
              'setVisitDescription',
              'Join the cast and crew of Homestead on-location in Utah during the filming of the TV series, with exclusive access for you and a guest.',
            ),
            disclaimer: t(
              'setVisitDisclaimer',
              'Does not include travel, lodging, and food expenses. Although you’ll have on-location access, we cannot promise interactions with the cast or crew.',
            ),
          },
        ],
        galleryImages: [
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952139/angel-studios/guild/homestead/homestead-img-01.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952142/angel-studios/guild/homestead/homestead-img-04.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952141/angel-studios/guild/homestead/homestead-img-02.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952142/angel-studios/guild/homestead/homestead-img-07.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952143/angel-studios/guild/homestead/homestead-img-08.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952144/angel-studios/guild/homestead/homestead-img-09.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952145/angel-studios/guild/homestead/homestead-img-05.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952145/angel-studios/guild/homestead/homestead-img-10.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952151/angel-studios/guild/homestead/homestead-img-06.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952148/angel-studios/guild/homestead/homestead-img-03.webp',
            alt: 'Homestead Images',
          },
        ],
      }
    }

    if (theatricalSlug === slugs.braveTheDark) {
      return {
        heroTitle: t(
          'giveawayHeroTitleBraveAlt',
          'Watch Brave the Dark in Theaters and Unlock a Chance to Win a $200k Dream Vacation',
        ),
        logoUrl:
          'https://images.angelstudios.com/image/upload/v1732124324/studio-app/catalog/94847f75-91ef-43ac-9c59-5ced2b9d9240.webp',
        inTheatersText: t('inTheatersBraveTheDark', 'In theaters Jan. 24th'),
        giveawayTrailerUrl: 'https://www.youtube-nocookie.com/embed/knvKDPwWo3I',
        trailerUrl: 'https://www.youtube-nocookie.com/embed/643rJ7pl5p4',
        backgroundVideoSrc: 'https://content.angelstudios.com/brave-the-dark/btd-video-loop-01.mp4',
        videoImage: '/v1733333895/100k-vacation-image-02.webp',
        backgroundHeroImage: {
          mobile: 'https://images.angelstudios.com/image/upload/v1734061752/brave-the-dark-vert-hero-img-01.webp',
          desktop: 'https://images.angelstudios.com/image/upload/v1734061720/brave-the-dark-key-horiz-01.webp',
        },
        share: {
          shareText:
            'The upcoming @AngelStudios movie Brave the Dark in theaters on Jan. 24th. Pre-order tickets for as chance to Win $100k Dream Vacations for You and Your Favorite Teacher in the #DreamGiveawaySweepstakes.',
          hashtag: '#DreamGiveawaySweepstakes',
          shareImage: 'v1734115533/brave-the-dark/ca4d3b1841bdc4ad201c3001746f8526.webp',
        },
        endDate: new Date(2025, 2, 9),
        winnerAnnouncedDate: new Date(2025, 3, 4),
        videoButtonText: t('watchVideoGiveaway', 'Watch Video'),
        projectName: 'Brave the Dark',
        productTitleToFilter: 'Brave the Dark Ticket',
        defaultTicketPackId: 'gid://shopify/Product/9065209790695',
        entriesProductId: 'gid://shopify/ProductVariant/50567428440295',
        rulesLink: 'https://bit.ly/200KDreamGiveaway',
        steps: [
          {
            title: t('preOrderTicketsStep1Title', 'Pre-Order Tickets'),
            description: t(
              'preOrderTicketsStep1Description',
              'Choose a ticket pack from our exclusive pre-order options.',
            ),
          },
          {
            title: t('preOrderTicketsStep2Title', 'Get Your Promo Codes'),
            description: t(
              'preOrderTicketsStep2Description',
              'Receive promo codes equal to your ticket quantity, redeemable when showtimes are available.',
            ),
          },
          {
            title: t('preOrderTicketsStep3Title', 'Multiply Your Chances'),
            description: t(
              'preOrderTicketsStep3DescriptionBrave',
              'The earlier you pre-order, the higher your multiplier for the $200k Dream Vacation Giveaway.',
            ),
          },
        ],
        tabs: [
          {
            title: t('details', 'Details'),
            type: GiveawayTabEnum.Details,
          },
          {
            title: t('faq', 'FAQ'),
            type: GiveawayTabEnum.Faq,
          },
        ],
        ticketFlow: {
          congratsModal: {
            text: t(
              'BtDCongratsModalText',
              "You've earned entries for your chance to win a $200,000 dream vacation! Complete your checkout to secure your entries.",
            ),
            image: 'v1735863876/brave-the-dark/dream-vacation-giveaway-sq-img-01.webp',
            alt: '',
          },
          summaryPage: {
            image: 'v1735863876/brave-the-dark/dream-vacation-giveaway-sq-img-01.webp',
            alt: '',
          },
          reservationCompletePage: {
            desktopImage: '/v1735864465/brave-the-dark/dream-vacation-giveaway-sq-img-01b_1.png',
            mobileImage: '/v1735864465/brave-the-dark/dream-vacation-giveaway-sq-img-01b_1.png',
            alt: '',
            text: t(
              'btdReservationCompleteText',
              'You’ve earned entries into the Brave the Dark Giveaway for a chance to win the $100k Dream Vacations for You and Your Favorite Teacher!',
            ),
          },
        },
        seo: {
          cloudinaryImagePath: 'v1734110113/open-graph-brave-giveaway-241212.webp',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('ticketsSEODescriptionPreSalesV2', 'PRE-ORDER {{projectName}} Tickets | Angel Studios', {
            projectName: 'Brave the Dark',
          }),
          description: t(
            'braveTheDarkTicketsSeoTitle',
            'Buy {{projectName}} tickets for a Chance to Win $100K Dream Vacations!',
            {
              projectName: 'Brave the Dark',
            },
          ),
          locale,
          path: isGiveawayRoute ? `/giveaway/${theatricalSlug}` : `/pre-sales/${theatricalSlug}/giveaway`,
        },
        faqs,
        galleryImages: [
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-01.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-02.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-03.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-04.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-05.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-06.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-07.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-08.webp',
            alt: 'Brave The Dark Images',
          },
        ],
      }
    }

    if (theatricalSlug === slugs.ruleBreakers) {
      return {
        heroTitle: t(
          'giveawayHeroTitleRuleBreakersV3',
          'Watch Rule Breakers in Theaters and Unlock a Chance to Win a $200k Dream Vacation.',
        ),
        endDate: new Date(2025, 2, 9),
        winnerAnnouncedDate: new Date(2025, 3, 4),
        seo: {
          cloudinaryImagePath: 'v1738359977/open-graph-rulebreakers-giveaway-250131_w34jho.webp',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('ticketsSEODescriptionPreSalesRuleBreakers', 'PRE-ORDER Rule Breakers Tickets | Angel Studios'),
          description: t(
            'ruleBreakerTicketsSeoTitle',
            'Buy Rule Breakers tickets for a Chance to Win $100K Dream Vacations!',
          ),
          locale,
          path: isGiveawayRoute ? `/giveaway/${theatricalSlug}` : `/pre-sales/${theatricalSlug}/giveaway`,
        },
        faqs,
        logoUrl: 'https://images.angelstudios.com/image/upload/v1734631651/rule-breakers-logo_1.webp',
        inTheatersText: t('inTheatersRuleBreakers', 'In theaters Mar. 7th'),
        share: {
          shareText:
            'The upcoming @AngelStudios movie Rule Breakers in theaters on Mar. 7th. Pre-order tickets for as chance to Win $100k Dream Vacations for You and Your Favorite Teacher in the #DreamGiveawaySweepstakes.',
          hashtag: '#DreamGiveawaySweepstakes',
          shareImage: 'v1734728616/rule-breakers-title-art-horiz-06.webp',
        },
        rulesLink: 'https://bit.ly/200KDreamGiveaway',
        videoImage: '/v1734728695/200k-vacation-image-01.webp',
        backgroundVideoSrc: 'https://content.angelstudios.com/rule-breakers/rule-breakers-video-loop2.mp4',
        defaultTicketPackId: 'gid://shopify/Product/9074595987687',
        entriesProductId: 'gid://shopify/ProductVariant/50586232881383',
        giveawayTrailerUrl: 'https://www.youtube-nocookie.com/embed/CW_P4zT6i9A',
        trailerUrl: 'https://www.youtube-nocookie.com/embed/CW_P4zT6i9A',
        videoButtonText: t('watchVideoGiveawayRule', 'Watch Video'),
        projectName: 'Rule Breakers',
        productTitleToFilter: 'Rule Breakers Ticket',
        backgroundHeroImage: {
          desktop: 'https://images.angelstudios.com/image/upload/v1738360036/rule-breakers-key-art-horiz-01_nowbpl.jpg',
          mobile: 'https://images.angelstudios.com/image/upload/v1738360035/rule-breakers-key-art-vert-01_h306kw.webp',
        },
        steps: [
          {
            title: t('preOrderTicketsStep1Title', 'Pre-Order Tickets'),
            description: t('selectTicketsStep1DescriptionRule', 'Select a ticket pack from our exclusive options.'),
          },
          {
            title: t('preOrderTicketsStep2Title', 'Get Your Promo Codes'),
            description: t(
              'preOrderTicketsStep2DescriptionRule',
              'Receive promo codes equal to your ticket quantity, redeemable when showtimes are available.',
            ),
          },
          {
            title: t('preOrderTicketsStep3TitleRuleBreakers', 'Get a Chance to Win the Dream Vacation'),
            description: t(
              'preOrderTicketsStep3DescriptionBraveBreakers',
              'Enter for your chance to win the $200K Dream Vacation Giveaway! This is your vacation and your mentor’s vacation—no need to travel together. Whether you’re soaking up the sun in the Caribbean while your mentor is hitting the slopes in the Alps, the choice is yours!',
            ),
          },
        ],
        tabs: [
          {
            title: t('details', 'Details'),
            type: GiveawayTabEnum.Details,
          },
          {
            title: t('faq', 'FAQ'),
            type: GiveawayTabEnum.Faq,
          },
        ],
        ticketFlow: {
          congratsModal: {
            text: t(
              'ruleBreakersCongratsModalText',
              "You've earned entries for your chance to win a $200,000 dream vacation! Complete your checkout to secure your entries.",
            ),
            image: 'v1735863876/brave-the-dark/dream-vacation-giveaway-sq-img-01.webp',
            alt: '',
          },
          summaryPage: {
            image: 'v1735863876/brave-the-dark/dream-vacation-giveaway-sq-img-01.webp',
            alt: '',
          },
          reservationCompletePage: {
            desktopImage: '/v1735864465/brave-the-dark/dream-vacation-giveaway-sq-img-01b_1.png',
            mobileImage: '/v1735864465/brave-the-dark/dream-vacation-giveaway-sq-img-01b_1.png',
            alt: '',
            text: t(
              'ruleBreakersReservationCompleteText',
              'You’ve earned entries into the Rule Breakers Giveaway for a chance to win the $100k Dream Vacations for You and Your Favorite Teacher!',
            ),
          },
        },
        galleryImages: [
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g01.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g02.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g03.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g04.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g05.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g06.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g07.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g08.webp',
            alt: 'Rule Breakers Images',
          },
        ],
      }
    }

    if (theatricalSlug === 'rule-breakers-tech') {
      return {
        heroTitle: t(
          'giveawayHeroTitleRuleBreakersV5',
          'Watch Rule Breakers in Theaters and enter to Win the Dream Tech Giveaway',
        ),
        endDate: new Date(2025, 2, 9),
        winnerAnnouncedDate: new Date(2025, 3, 4),
        seo: {
          cloudinaryImagePath: 'v1738636614/open-graph-rulebreakers-tech-giveaway-250203_vbefrg.webp',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('ticketsSEODescriptionPreSalesRuleBreakers', 'PRE-ORDER Rule Breakers Tickets | Angel Studios'),
          description: t(
            'ruleBreakerTicketsSeoTitleTechV2',
            'Buy Rule Breakers tickets for a Chance to Win the Dream Tech Giveaway!',
          ),
          locale,
          path: isGiveawayRoute ? `/giveaway/${theatricalSlug}` : `/pre-sales/${theatricalSlug}/giveaway`,
        },
        faqs,
        logoUrl: 'https://images.angelstudios.com/image/upload/v1734631651/rule-breakers-logo_1.webp',
        inTheatersText: t('inTheatersRuleBreakers', 'In theaters Mar. 7th'),
        share: {
          shareText:
            'The upcoming @AngelStudios movie Brave the Dark in theaters on Jan. 24th. Pre-order tickets for as chance to Win the Dream Tech Giveaway!',
          hashtag: '#DreamGiveawaySweepstakes',
          shareImage: 'v1738360035/rule-breakers-key-art-vert-01_h306kw.webp',
        },
        rulesLink: 'https://bit.ly/RuleBreakersTech',
        videoImage: '/v1738690355/rule-breakers-tech-giveaway-img-01_zeaqms.webp',
        backgroundVideoSrc: 'https://content.angelstudios.com/rule-breakers/rule-breakers-video-loop2.mp4',
        defaultTicketPackId: 'gid://shopify/Product/9074595987687',
        entriesProductId: 'gid://shopify/ProductVariant/50685440360679',
        giveawayTrailerUrl: 'https://www.youtube-nocookie.com/embed/CW_P4zT6i9A',
        trailerUrl: 'https://www.youtube-nocookie.com/embed/CW_P4zT6i9A',
        videoButtonText: t('watchVideoGiveawayRule', 'Watch Video'),
        projectName: 'Rule Breakers',
        productTitleToFilter: 'Rule Breakers Ticket',
        backgroundHeroImage: {
          desktop:
            'https://images.angelstudios.com/image/upload/v1738360036/rule-breakers-key-art-horiz-01_nowbpl.webp',
          mobile: 'https://images.angelstudios.com/image/upload/v1738360035/rule-breakers-key-art-vert-01_h306kw.webp',
        },
        steps: [
          {
            title: t('preOrderTicketsStep1Title', 'Pre-Order Tickets'),
            description: t('selectTicketsStep1DescriptionRule', 'Select a ticket pack from our exclusive options.'),
          },
          {
            title: t('preOrderTicketsStep2Title', 'Get Your Promo Codes'),
            description: t(
              'preOrderTicketsStep2DescriptionRule',
              'Receive promo codes equal to your ticket quantity, redeemable when showtimes are available.',
            ),
          },
          {
            title: t('preOrderTicketsStep3TitleRuleBreakersTech', 'Enter to Win the Tech Giveaway'),
            description: t(
              'preOrderTicketsStep3DescriptionBraveBreakersTech',
              'Pre-order now for your chance to win an incredible tech bundle valued at $15,000! This prize includes everything an aspiring engineer or robotics enthusiast could dream of—including a Macbook Pro, 3D printer, cutting-edge gaming gear, STEM electronics exploration kit, robotics LEGO sets, and even a pre-built gaming PC. Whether you’re coding, building, or exploring new tech frontiers, this all-in-one collection has the tools to ignite your creativity!',
            ),
          },
        ],
        tabs: [
          {
            title: t('prizeItemsTech', 'Prize Items'),
            type: GiveawayTabEnum.Prizes,
          },
          {
            title: t('details', 'Details'),
            type: GiveawayTabEnum.Details,
          },
          {
            title: t('faq', 'FAQ'),
            type: GiveawayTabEnum.Faq,
          },
        ],
        prizes: [
          {
            image: '/v1738636957/MacBook-Pro-1b_jdwdud.webp',
            alt: 'MacBook',
            title: '16” Apple MacBook Pro (M3 Pro)',
            value: '$3,500',
          },
          {
            image: '/v1738637008/3-iPad-Airs-1b_vswvhf.webp',
            alt: 'iPad',
            title: 'Three (3) iPad Air’s',
            value: '$1,800',
          },
          {
            image: '/v1738637037/nzxt-computer-02_kceyvk.webp',
            alt: 'Desktop',
            title: 'NZXT Pre-built Gaming PC',
            value: '$1,740',
          },
          {
            image: '/v1738637105/3D-Printer-3_ix9yxs.webp',
            alt: '3D Printer',
            title: 'Bambu X1 Carbon 3D Printer Pack',
            value: '$1,700',
          },
          {
            image: '/v1738637159/Celestron-Telescope-2_wu56i9.webp',
            alt: 'Celestron Nexstar',
            title: 'Celestron Nexstar 8SE',
            value: '$1,600',
          },
          {
            image: '/v1738637204/iPad-Pro-_-Apple-Pencil-3_qqfhgn.webp',
            alt: 'iPad Pro',
            title: 'Apple iPad Pro 12.9” (w/ Apple Pencil)',
            value: '$1,400',
          },
          {
            image: '/v1738637266/2-Meta-Quest-3S-2_dfgevh.webp',
            alt: '2 Meta Quest',
            title: '2 Meta Quest 3’s + Gaming Gift Card',
            value: '$1,200',
          },
          {
            image: '/v1738637323/Mark-Rober_s-Crunch-Labs_ueoyli.webp',
            alt: 'Crunch Labs',
            title: '1 Year of Mark Rober’s Crunch Labs Hack Pack',
            value: '$876',
          },
          {
            image: '/v1738637423/Lego-Education-Spike-Set-1_rvmqnd.webp',
            alt: 'Legos',
            title: 'Lego Education Spike Set + Expansion Set',
            value: '$500',
          },
          {
            image: '/v1738780959/crunch-labs-build-box-img-01_ub1tr5.webp',
            alt: 'Mark Rober’s Crunch Labs',
            title: '1 Year of Mark Rober’s Crunch Labs',
            value: '$330',
          },
          {
            image: '/v1738637589/Snaptain-Drone-1_z6ku7l.webp',
            alt: 'Drone',
            title: 'Ryze Tech Tello Drone',
            value: '$219',
          },
          {
            image: '/v1738637630/Lego-Artemis-Space-Launch-System-3_hrzwoi.webp',
            alt: 'Legos',
            title: 'Lego Artemis Space Launch System',
            value: '$260',
          },
          {
            image: '/v1738637688/wonder-workshop-dash-robot_ajdcqh.webp',
            alt: 'Wonder Workshop',
            title: 'Wonder Workshop Dash Robot',
            value: '$180',
          },
          {
            image: '/v1738637735/Snap-Circuits-Exploration-Kit-2_xg9jc5.webp',
            alt: 'Snap Circuits',
            title: 'Snap Circuits Extreme Electronics Exploration Kit',
            value: '$126',
          },
          {
            image: '/v1738637793/Osmo-Genius-Starter-Kit-1b_z4vloc.webp',
            alt: 'Snap Circuits',
            title: 'Osmo Genius Starter Kit',
            value: '$65',
          },
        ],
        galleryImages: [
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g01.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g02.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g03.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g04.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g05.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g06.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g07.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g08.webp',
            alt: 'Rule Breakers Images',
          },
        ],
      }
    }

    if (theatricalSlug === slugs.kingOfKings) {
      return {
        heroTitle: t(
          'giveawayHeroTitleKingOfKingsV2',
          'Watch The King of Kings in Theaters and enter to Win a $100K Unforgettable Journey!',
        ),
        endDate: new Date(2025, 2, 9),
        winnerAnnouncedDate: new Date(2025, 3, 27),
        seo: {
          cloudinaryImagePath: 'v1738950355/open-graph-rulebreakers-tech-giveaway-250203_d4gtev.webp',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('ticketsSEODescriptionPreSalesKingOfKings', 'PRE-ORDER King of Kings Tickets | Angel Studios'),
          description: t(
            'kingOfKingsTicketsSeoTitle',
            'Buy King of Kings tickets for a Chance to Win the $100k Giveaway!',
          ),
          locale,
          path: isGiveawayRoute ? `/giveaway/${theatricalSlug}` : `/pre-sales/${theatricalSlug}/giveaway`,
        },
        faqs,
        backgroundVideoSrc: 'https://content.angelstudios.com/king-of-kings/tkok-hero-vid-02a.mp4',
        logoUrl: 'https://images.angelstudios.com/image/upload/v1738950570/TKoK_Logo-White_1_odfsyb.webp',
        inTheatersText: t('inTheatersKingOfKings', 'In theaters Apr. 11th'),
        share: {
          shareText:
            'The upcoming @AngelStudios movie King of Kings in theaters on Apr. 11th. Pre-order tickets for as chance to Win the $100k Giveaway!',
          hashtag: '',
          shareImage: 'v1738950712/king-of-kings-giveaway-hero-vert-02_ztlgjf.webp',
        },
        giveawayValue: '$100k',
        rulesLink: 'https://bit.ly/AngelFilmsKingofKings',
        videoImage: '/v1738950773/kok-video-cover-img-01_o8d3ng.webp',
        defaultTicketPackId: 'gid://shopify/Product/9110537044199',
        entriesProductId: 'gid://shopify/ProductVariant/50696728903911',
        giveawayTrailerUrl: 'https://www.youtube-nocookie.com/embed/85jmrZfkghE',
        trailerUrl: 'https://www.youtube-nocookie.com/embed/-l3SkYQcOvI',
        videoButtonText: t('watchGiveawayVideo', 'Watch Giveaway Video'),
        projectName: 'The King of Kings',
        productTitleToFilter: 'King of Kings Ticket',
        backgroundHeroImage: {
          desktop:
            'https://images.angelstudios.com/image/upload/v1738950712/king-of-kings-giveaway-hero-horiz-01_zjqdlj.webp',
          mobile:
            'https://images.angelstudios.com/image/upload/v1738950712/king-of-kings-giveaway-hero-vert-02_ztlgjf.webp',
        },
        steps: [
          {
            title: t('preOrderTicketsStep1Title', 'Pre-Order Tickets'),
            description: t('selectTicketsStep1DescriptionRule', 'Select a ticket pack from our exclusive options.'),
          },
          {
            title: t('preOrderTicketsStep2Title', 'Get Your Promo Codes'),
            description: t(
              'preOrderTicketsStep2DescriptionRule',
              'Receive promo codes equal to your ticket quantity, redeemable when showtimes are available.',
            ),
          },
          {
            title: t('preOrderTicketsStep3KingOfKings', 'Multiply Your Chances'),
            description: t(
              'preOrderTicketsStep3DescriptionKingOfKingsV2',
              'The earlier you pre-order, the higher your multiplier for The King of Kings Giveaway.',
            ),
          },
        ],
        tabs: [
          {
            title: t('prizeItemsTech', 'Prize Items'),
            type: GiveawayTabEnum.Prizes,
          },
          {
            title: t('details', 'Details'),
            type: GiveawayTabEnum.Details,
          },
          {
            title: t('faq', 'FAQ'),
            type: GiveawayTabEnum.Faq,
          },
        ],
        prizes: [
          {
            image: 'https://images.angelstudios.com/image/upload/v1738950989/isreal-dome-image-01_ss9xf1.jpg',
            alt: 'Trip Israel',
            title: 'Trip Package to Israel',
            value: '$26,948',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1727735729/Homestead/prizes/bitcoin-01.jpg',
            alt: 'Bitcoin',
            title: '$25,000 in Bitcoin or Cash',
            value: '$25,000',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1738950985/vacation-london-image-01_zuklcq.jpg',
            alt: 'Trip London',
            title: 'Trip Package to London',
            value: '$21,032',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739402042/kok-giveaway-tv-img-01_msjty9.jpg',
            alt: 'TV',
            title: 'LG OLED 83” TV',
            value: '$5,000',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1738950991/movie-theater-chairs-01_fetvpe.jpg',
            alt: 'Two Flex HR Theater Chairs',
            title: 'Two Flex HR Theater Chairs',
            value: '$2,900',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739402042/kok-giveaway-speakers-img-01_dmwtgy.jpg',
            alt: 'Sonos Ultimate Immersive',
            title: 'Sonos Ultimate Immersive',
            value: '$2,696',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1738950989/amgel-guild-img-01_fl4i7j.jpg',
            alt: '5-Year Angel Guild Membership (non-transferrable)',
            title: '5-Year Angel Guild Membership (non-transferrable)',
            value: '$1,200',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739402042/kok-giveaway-redvines-img-01_jq9ucp.jpg',
            alt: 'Pallet of Redvines Candy',
            title: 'Pallet of Redvines Candy',
            value: '$1,215',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739402043/kok-giveaway-chocolate-img-01_q5ez5f.jpg',
            alt: 'Variety pack of Chocolate',
            title: 'Variety pack of Chocolate',
            value: '$1,007',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1738950984/box-office-image-01_kkihz5.jpg',
            alt: 'Show at West End for Four',
            title: 'Show at West End for Four',
            value: '$600',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739402042/kok-giveaway-switch-img-01_lfbg2o.jpg',
            alt: 'Nintendo Switch Package',
            title: 'Nintendo Switch Package',
            value: '$500',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739402043/kok-giveaway-fridge-img-01_f8fngp.jpg',
            alt: 'Newair Pismatic Can Beverage Fridge',
            title: 'Newair Pismatic Can Beverage Fridge',
            value: '$500',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1738950987/beanbag-image-01_pzd8pa.jpg',
            alt: 'Ultimate Sack 5000: Bean Bag Chair',
            title: 'Ultimate Sack 5000: Bean Bag Chair',
            value: '$430',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739402042/kok-giveaway-popcorn-img-01_pscrge.jpg',
            alt: 'Gold Medal Mega Pop Popcorn',
            title: 'Gold Medal Mega Pop Popcorn',
            value: '$256',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1738950989/popcorn-machine-01_hhddb6.jpg',
            alt: 'Nostalgia Popcorn Maker Machine',
            title: 'Nostalgia Popcorn Maker Machine',
            value: '$240',
          },
          {
            image:
              'https://images.angelstudios.com/image/upload/v1738950984/historic-home-interior-image-01_ziz12c.jpg',
            alt: 'Charles Dickens Museum Tour in London',
            title: 'Charles Dickens Museum Tour in London',
            value: '$200',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739402042/kok-giveaway-appleTV-img-01_ce8eiv.jpg',
            alt: 'Apple TV 4K',
            title: 'Apple TV 4K',
            value: '$150',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1738950989/candy-image-shelf-01_fmmrq0.jpg',
            alt: 'Bachel Rustic Snack Cabinet',
            title: 'Bachel Rustic Snack Cabinet',
            value: '$130',
          },
        ],
        galleryImages: [
          {
            src: 'https://images.angelstudios.com/image/upload/v1738951554/KOK_Google-Search-Images_1.91x1_V3_n8qoqx.jpg',
            alt: 'King of Kings Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1738951554/KOK_Google-Search-Images_1.91x1_V4_bssggs.jpg',
            alt: 'King of Kings Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1738951555/KOK_Google-Search-Images_1.91x1_V5_bztj2y.jpg',
            alt: 'King of Kings Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1738951556/KOK_Google-Search-Images_1.91x1_V6_io7abi.jpg',
            alt: 'King of Kings Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1738951555/KOK_Google-Search-Images_1.91x1_V7_jhpkuw.jpg',
            alt: 'King of Kings Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1738951555/KOK_Google-Search-Images_1.91x1_V8_enrlcz.jpg',
            alt: 'King of Kings Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1738951556/KOK_Google-Search-Images_1.91x1_V9_uusowp.jpg',
            alt: 'King of Kings Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1738951556/KOK_Google-Search-Images_1.91x1_V10_ttuohl.jpg',
            alt: 'King of Kings Images',
          },
        ],
      }
    }

    if (theatricalSlug === slugs.theLastRodeo) {
      return {
        heroTitle: t(
          'giveawayHeroTitleLastRodeoV2',
          'See The Last Rodeo in Theaters & Get Entries for the $350k Giveaway',
        ),
        endDate: new Date(2025, 5, 7),
        winnerAnnouncedDate: new Date(2025, 5, 7),
        seo: {
          cloudinaryImagePath: 'v1739572055/open-graph-last-rodeo-giveaway-250214_jhayfk.jpg',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('ticketsSEODescriptionPreSalesLastRodeo', 'PRE-ORDER The Last Rodeo Tickets | Angel Studios'),
          description: t(
            'theLastRodeoTicketsSeoTitle',
            'Buy The Last Rodeo tickets for a Chance to Win the $350k Giveaway!',
          ),
          locale,
          path: isGiveawayRoute ? `/giveaway/${theatricalSlug}` : `/pre-sales/${theatricalSlug}/giveaway`,
        },
        faqs,
        giveawayValue: '$350,000',
        logoUrl: 'https://images.angelstudios.com/image/upload/v1739566078/last-rodeo-logo-white_t34gvh.webp',
        inTheatersText: t('inTheatersLastRodeo', 'In theaters May 23rd'),
        share: {
          shareText:
            'The upcoming @AngelStudios movie The Last Rodeo in theaters on May. 23rd. Pre-order tickets for as chance to Win the $350k Giveaway! #LastRodeoSweepstakes',
          hashtag: '#LastRodeoSweepstakes',
          shareImage:
            'https://images.angelstudios.com/image/upload/v1739572142/The-Last-Rodeo---Official-Key-Art---Square---TEXTLESS_jgme9r.webp',
        },
        rulesLink: 'https://bit.ly/TheLastRodeo',
        videoImage:
          'https://images.angelstudios.com/image/upload/v1739572486/last-rode-temp-key-wide-03f_1_cem2ka.webp',
        backgroundVideoSrc: 'https://content.angelstudios.com/the-last-rodeo/last-rodeo-hero-vid-loop-02a.mp4',
        defaultTicketPackId: 'gid://shopify/Product/9115824259303',
        entriesProductId: 'gid://shopify/ProductVariant/50704541679847',
        giveawayTrailerUrl: 'https://www.youtube-nocookie.com/embed/emeykl37eC8',
        trailerUrl: 'https://www.youtube-nocookie.com/embed/emeykl37eC8',
        videoButtonText: t('watchVideoGiveawayRule', 'Watch Video'),
        projectName: 'The Last Rodeo',
        productTitleToFilter: 'The Last Rodeo Ticket',
        backgroundHeroImage: {
          desktop:
            'https://images.angelstudios.com/image/upload/v1739572143/The_Last_Rodeo_-_Official_Key_Art_-_16x9_-_DIGITAL_-_TEXTLESS_uksohl.webp',
          mobile:
            'https://images.angelstudios.com/image/upload/v1739572142/The_Last_Rodeo_-_Official_Key_Art_-_27x40_-_DIGITAL_-_TEXTLESS_usafbn.webp',
        },
        steps: [
          {
            title: t('preOrderTicketsStep1Title', 'Pre-Order Tickets'),
            description: t('selectTicketsStep1DescriptionRule', 'Select a ticket pack from our exclusive options.'),
          },
          {
            title: t('preOrderTicketsStep2Title', 'Get Your Promo Codes'),
            description: t(
              'preOrderTicketsStep2DescriptionRule',
              'Receive promo codes equal to your ticket quantity, redeemable when showtimes are available.',
            ),
          },
          {
            title: t('preOrderTicketsStep3KingOfKings', 'Multiply Your Chances'),
            description: t(
              'preOrderTicketsStep3DescriptionLastRodeoV2',
              'The earlier you pre-order, the higher your bonus entries for The Last Rodeo Giveaway when you pre-order 4 or more tickets.',
            ),
          },
        ],
        tabs: [
          {
            title: t('prizeItemsTech', 'Prize Items'),
            type: GiveawayTabEnum.Prizes,
          },
          {
            title: t('details', 'Details'),
            type: GiveawayTabEnum.Details,
          },
          {
            title: t('faq', 'FAQ'),
            type: GiveawayTabEnum.Faq,
          },
        ],
        prizes: [
          {
            image:
              'https://images.angelstudios.com/image/upload/v1739420863/imperial-outdoors-trailer-img-01_yft8nz.jpg',
            alt: 'Imperial Outdoors Xplore X195 Travel Trailer',
            title: 'Imperial Outdoors Xplore X195 Travel Trailer',
            value: '$147,000',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1727735729/Homestead/prizes/bitcoin-01.jpg',
            alt: 'Bitcoin',
            title: '$100,000 in BitCoin',
            value: '$100,000',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739420803/f150-truck-img-01_r9wr47.jpg',
            alt: 'Ford F150 Raptor',
            title: 'Ford F150 Raptor',
            value: '$83,000',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739420646/AdobeStock_917699608_v2g2hw.jpg',
            alt: 'Kubota Zero Turn Mower',
            title: 'Kubota - Z252KW-42 - Zero Turn Mower',
            value: '$7,499',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739420646/AdobeStock_176856820_r9okkt.jpg',
            alt: 'Whole Cow',
            title: 'Whole Cow of Meat with Freezer',
            value: '$6,876',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739420646/AdobeStock_763161907_lzmtkh.jpg',
            alt: 'Warn Winch',
            title: 'Warn ZEON XP 14-S Winch',
            value: '$3,200',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739420646/AdobeStock_555551176_sfrjvv.jpg',
            alt: 'Vehicle Recovery Kit',
            title: 'Factor 55 - Vehicle Recovery Kit',
            value: '$1,640',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739420646/AdobeStock_1221667798_c4mzms.jpg',
            alt: 'Red Ultra Hook',
            title: 'Factor 55 - Red Ultra Hook',
            value: '$280',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739420647/AdobeStock_1106386350_y5swrt.jpg',
            alt: 'Gift Card for Ariat Denim',
            title: 'Gift Card for Ariat Denim',
            value: '$280',
          },
          {
            image: 'https://images.angelstudios.com/image/upload/v1739420646/AdobeStock_247660397_biuhjn.jpg',
            alt: 'Gift Card for Ariat Cowboy Boots',
            title: 'Gift Card for Ariat Cowboy Boots',
            value: '$560',
          },
        ],
        galleryImages: [
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420973/last-rodeo-gal-img-01_qxhelf.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420973/last-rodeo-gal-img-02_v5yntb.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420972/last-rodeo-gal-img-03_fmdozw.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420973/last-rodeo-gal-img-04_catswu.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420972/last-rodeo-gal-img-05_b6u9bu.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420972/last-rodeo-gal-img-06_vbdnr6.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420972/last-rodeo-gal-img-07_aqualt.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420972/last-rodeo-gal-img-08_veyaod.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420973/last-rodeo-gal-img-09_vapnrq.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1739420972/last-rodeo-gal-img-10_bspj0a.jpg',
            alt: 'The Last Rodeo Gallery Image',
          },
        ],
        partnerLogos: [
          {
            src: '/v1739480251/partner-logo-imperial-outdoors_ggacqd.png',
            alt: 'imperial-outdoors-logo',
          },
          {
            src: '/v1739480241/partner-logo-factor-55_eegm7d.png',
            alt: 'factor-55-logo',
          },
          {
            src: '/v1739480246/partner-logo-kubota_zv9kmj.png',
            alt: 'kubota-logo',
          },
          {
            src: '/v1739480256/partner-logo-ariat_gnnqab.png',
            alt: 'ariat-logo',
          },
        ],
      }
    }

    return null
  }, [faqs, isGiveawayRoute, locale, t, theatricalSlug])

  if (!data) return null

  return { ...data, isActive: isGiveawayActive(data.endDate) } as GiveawayType
}

export const isGiveawayActive = (endDate: Date) => {
  const timeZone = 'America/Denver'
  const parsedEndDate = toZonedTime(new Date(endDate), timeZone)
  const startOfEndDate = startOfDay(parsedEndDate)
  const startOfToday = startOfDay(toZonedTime(new Date(), timeZone))
  return isEqual(startOfEndDate, startOfToday) || isAfter(startOfEndDate, startOfToday)
}
